import React from 'react';
import { TabsContent } from '@/components/ui/tabs';
import OptimizedPageViewsChart from '../charts/OptimizedPageViewsChart';
import OptimizedUserActionsChart from '../charts/OptimizedUserActionsChart';
import DailyActivityChart from '../charts/DailyActivityChart';
import TimeDistributionChart from '../charts/TimeDistributionChart';
import OptimizedPieChart from '../charts/OptimizedPieChart';

interface OptimizedTabContentProps {
  tabId: string;
  isLoading: boolean;
  data: any[];
  exportCSV: (data: any[], filename: string) => void;
  colors?: string[];
}

const OptimizedTabContent = React.memo(({ 
  tabId, 
  isLoading, 
  data, 
  exportCSV,
  colors = []
}: OptimizedTabContentProps) => {
  
  return (
    <TabsContent value={tabId}>
      {tabId === 'pages' && (
        <OptimizedPageViewsChart 
          pageStats={data} 
          loading={isLoading} 
          exportCSV={exportCSV} 
        />
      )}
      {tabId === 'actions' && (
        <OptimizedUserActionsChart 
          actionStats={data} 
          loading={isLoading} 
          exportCSV={exportCSV} 
        />
      )}
      {tabId === 'daily' && (
        <DailyActivityChart 
          dailyStats={data} 
          loading={isLoading} 
          exportCSV={exportCSV} 
        />
      )}
      {tabId === 'time' && (
        <TimeDistributionChart 
          timeStats={data} 
          loading={isLoading} 
          exportCSV={exportCSV} 
        />
      )}
      {tabId === 'platform' && (
        <OptimizedPieChart
          data={data}
          loading={isLoading}
          exportCSV={exportCSV}
          COLORS={colors}
          title="Platform Distribution"
          description="Breakdown of visits by device type"
          exportFilename="platform_stats"
          dataKey="count"
          nameKey="platform"
        />
      )}
      {tabId === 'browser' && (
        <OptimizedPieChart
          data={data}
          loading={isLoading}
          exportCSV={exportCSV}
          COLORS={colors}
          title="Browser Distribution"
          description="Breakdown of visits by browser type"
          exportFilename="browser_stats"
          dataKey="count"
          nameKey="browser"
          formatter={(value) => [`${value} visits`, 'Count']}
        />
      )}
      {tabId === 'os' && (
        <OptimizedPieChart
          data={data}
          loading={isLoading}
          exportCSV={exportCSV}
          COLORS={colors}
          title="Operating System Distribution"
          description="Breakdown of visits by operating system"
          exportFilename="os_stats"
          dataKey="count"
          nameKey="os"
          formatter={(value) => [`${value} visits`, 'Count']}
        />
      )}
      {tabId === 'language' && (
        <OptimizedPieChart
          data={data}
          loading={isLoading}
          exportCSV={exportCSV}
          COLORS={colors}
          title="Language Distribution"
          description="Breakdown of visits by preferred language"
          exportFilename="language_stats"
          dataKey="count"
          nameKey="language"
        />
      )}
      {tabId === 'sources' && (
        <OptimizedPieChart
          data={data}
          loading={isLoading}
          exportCSV={exportCSV}
          COLORS={colors}
          title="Traffic Sources"
          description="How users are finding your platform"
          exportFilename="referrer_stats"
          dataKey="count"
          nameKey="referrer_category"
          formatter={(value) => [`${value} visits`, 'Count']}
        />
      )}
      {tabId === 'exitpages' && (
        <OptimizedPageViewsChart 
          pageStats={data} 
          loading={isLoading} 
          exportCSV={exportCSV}
          title="Top Exit Pages"
          description="Pages where users most commonly leave"
          exportFilename="exit_pages"
        />
      )}
      {tabId === 'campaigns' && (
        <>
          <OptimizedPieChart
            data={data}
            loading={isLoading}
            exportCSV={exportCSV}
            COLORS={colors}
            title="UTM (Urchin Tracking Module) Campaign Performance"
            description="Traffic by marketing campaign source"
            exportFilename="utm_campaigns"
            dataKey="visits"
            nameKey="utm_campaign"
            formatter={(value) => [`${value} visits`, 'Visits']}
          />
          {!isLoading && data.length === 0 && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                No campaign tracking data found. UTM (Urchin Tracking Module) parameters are URL tags that track which marketing campaigns bring visitors to your site.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Example: <code className="bg-background px-2 py-1 rounded">?utm_source=google&utm_medium=cpc&utm_campaign=spring_sale</code>
              </p>
            </div>
          )}
        </>
      )}
    </TabsContent>
  );
});

OptimizedTabContent.displayName = 'OptimizedTabContent';

export default OptimizedTabContent;
